// Bouwtekening per onderdeel (O71), gedeelde code voor browser/viewer.ts (geen eigen bundel).
//
// Klik je in de 3D-viewer op een onderdeel, dan opent naast het model een kaart met de naam en de
// maten uit de zaaglijst, de tekening met maatvoering, het materiaal, de plaat of lat waar het uit
// komt, de afwerking en de knop "Laat zien hoe". Op een breed scherm zweeft de kaart als popup rechts
// naast het model, helemaal binnen het venster (O71-O2). Op een smal scherm staat de kaart als paneel
// onder het model. Sluiten met de knop, met Escape of met een tik naast het model.
//
// De tekening is dezelfde als in de bouw-PDF (O-120b): het bovenaanzicht met de lange kant erboven en
// de kopse kant rechts, alle maten buiten het hout en zonder botsingen (core/maatschets.ts). Geeft de
// viewer de driehoeken van het onderdeel mee, dan komt de vorm uit het 3D-model als die klopt met de
// zaaglijst (core/tekening.ts), net als in de PDF. Anders de tekening uit het ontwerp: de omtrek uit de
// zaagsneden, zodat ook de punt van een nestkast er staat. De invoer maakt de server
// (bouwtekening.ts, met core/tekening-ontwerp.ts) en staat als JSON in data-bouwtekening op .viewer.
//
// De tekening is SVG-tekst met vaste kleuren, zoals pdfmake die nodig heeft. De stylesheet hieronder
// zet die kleuren om naar de tokens van de site, zodat de tekening ook in het donkere thema klopt.

import { maatschetsSvg, type SchetsInvoer } from "../../../core/maatschets.ts";
import type { ZaaglijstStuk } from "../../../core/modeltekening.ts";
import { tekeningVanStuk, type Geometrie } from "../../../core/tekening.ts";

export type { Geometrie } from "../../../core/tekening.ts";

/** Zoals StukOntwerp in core/tekening-ontwerp.ts. */
export interface StukOntwerp {
  schets: SchetsInvoer;
  zaaglijst: ZaaglijstStuk;
  ankers: Array<[number, number]>;
  zonder_tekening: string | null;
}

/** Zoals OnderdeelTekening in ../bouwtekening.ts (die map valt buiten tsconfig.browser.json). */
export interface OnderdeelTekening {
  naam: string;
  aantal: number;
  lengte_mm: number;
  breedte_mm: number;
  dikte_mm: number;
  diameter_mm: number | null;
  materiaal: string;
  uit: string;
  afwerking: string | null;
  /** Beide uiteinden in het kort (D14-8); ontbreekt bij gegevens van voor D14-8. */
  uiteinden?: string | null;
  bewerking: string | null;
  verstek_graden: number;
  schuin_graden: number;
  gaten: Array<{ aantal: number; diameter_mm: number; diepte_mm: number | null; door: boolean }>;
  /** Gaten met plek (O93); ontbreekt bij gegevens van voor O93. */
  gatplekken?: GatPlek[];
  stap: { nr: number; tekst: string } | null;
  /** De tekening zoals in de bouw-PDF (O-120b); ontbreekt bij gegevens van daarvoor. */
  tekening?: StukOntwerp;
}

/** Zoals GatPlek in ../bouwtekening.ts: x langs de lengte, y langs de breedte, z vanaf het ondervlak, in mm. */
export interface GatPlek {
  x_mm: number;
  y_mm: number;
  z_mm?: number;
  diameter_mm: number;
  diepte_mm: number | null;
  door: boolean;
  vlak: "boven" | "onder" | "voor" | "achter" | "kop_links" | "kop_rechts";
}

/** Leest data-bouwtekening. Onbruikbare gegevens geven null: dan werkt de viewer zoals voorheen. */
export function leesGegevens(el: HTMLElement): Map<string, OnderdeelTekening> | null {
  const ruw = el.dataset.bouwtekening;
  if (!ruw) return null;
  try {
    const j = JSON.parse(ruw) as { delen?: Record<string, OnderdeelTekening> };
    if (!j.delen || typeof j.delen !== "object") return null;
    return new Map(Object.entries(j.delen));
  } catch {
    return null;
  }
}

/** 406.4 wordt "406,4"; 235 blijft "235". */
export function mm(w: number): string {
  const r = Math.round(w * 10) / 10;
  return String(r).replace(".", ",");
}

export function matenTekst(d: OnderdeelTekening): string {
  if (d.diameter_mm) return `Ø ${mm(d.diameter_mm)} × ${mm(d.dikte_mm)} mm`;
  return `${mm(d.lengte_mm)} × ${mm(d.breedte_mm)} × ${mm(d.dikte_mm)} mm`;
}

function gatTekst(g: OnderdeelTekening["gaten"][number]): string {
  const diepte = g.door ? ", door en door" : g.diepte_mm !== null ? `, ${mm(g.diepte_mm)} mm diep` : "";
  return `${g.aantal} × Ø ${mm(g.diameter_mm)} mm${diepte}`;
}

// ------------------------------------------------------------------ de tekening (O-120b)

/**
 * Breedte van de tekening in punten. De bouw-PDF tekent op 515 pt; de kaart op 360 pt, met dezelfde
 * regels. Smaller kan niet: drie aanzichten met hun maten krijgen dan te weinig ruimte (de poot van
 * opstapje-boog werd op 250 pt een bovenaanzicht van 35 pt breed). Op de kaart is een maat zo ongeveer
 * 7 px hoog; de knop "Tekening groter" toont dezelfde tekening op de breedte van het venster.
 */
const TEKENING_B = 360;
/** Hoogste tekening op de kaart, in punten. */
const TEKENING_H = 360;

/** De tekening op de kaart, of de zin waarom er geen is (zoals in de bouw-PDF). */
export type KaartTekening = { svg: string; noot: string; bron: "model" | "ontwerp" } | { zonder: string };

/**
 * De tekening van dit onderdeel, zoals in de bouw-PDF: uit het 3D-model als `geo` er is en klopt met de
 * zaaglijst, anders uit het ontwerp. Heeft een gat in het ontwerp geen plek, dan geen tekening maar de zin
 * uit de PDF. Null bij gegevens zonder tekening of als er iets misgaat.
 */
export function kaartTekening(d: OnderdeelTekening, geo: Geometrie | null | undefined): KaartTekening | null {
  const t = d.tekening;
  if (!t) return null;
  try {
    const uit = tekeningVanStuk(t.schets, geo, t.zaaglijst, t.ankers);
    if (uit.bron !== "model" && t.zonder_tekening) return { zonder: t.zonder_tekening };
    const s = maatschetsSvg(uit.schets, { breedte: TEKENING_B, maxHoogte: TEKENING_H });
    return { svg: s.svg, noot: s.noot, bron: uit.bron };
  } catch {
    return null;
  }
}

/** De SVG-tekst als element, met een klasse voor de stylesheet en een naam voor een schermlezer. */
function svgElement(svg: string, naam: string): SVGSVGElement | null {
  const sjabloon = document.createElement("template");
  sjabloon.innerHTML = svg;
  const el = sjabloon.content.firstElementChild;
  if (!(el instanceof SVGSVGElement)) return null;
  // Zonder vaste maten schaalt de tekening mee met de breedte van de kaart (de viewBox blijft).
  el.removeAttribute("width");
  el.removeAttribute("height");
  el.setAttribute("class", "bt-svg");
  el.setAttribute("role", "img");
  el.setAttribute("aria-label", `Bouwtekening van ${naam}`);
  return el;
}

/** De regel bij Bewerking zonder "Recht afkorten": als alles recht is, hoef je dat niet te noemen (Stijn, 29-09). */
function bewerkingTekst(bewerking: string | null): string {
  return (bewerking ?? "")
    .replace(/(?:^|,\s*)recht\s+afkorten(?=\s*,|$)/ig, "")
    .replace(/^,\s*/, "")
    .trim();
}

/** De regel bij Gaten: uit de bewerking, of uit de gaten met plek als de bewerking ze niet noemt. */
function gatenTekst(d: OnderdeelTekening): string {
  if (d.gaten.length) return d.gaten.map(gatTekst).join(". ");
  const groepen = new Map<string, OnderdeelTekening["gaten"][number]>();
  for (const p of d.gatplekken ?? []) {
    const sleutel = `${mm(p.diameter_mm)}|${p.door}|${p.diepte_mm}`;
    const g = groepen.get(sleutel);
    if (g) g.aantal++;
    else groepen.set(sleutel, { aantal: 1, diameter_mm: p.diameter_mm, diepte_mm: p.diepte_mm, door: p.door });
  }
  return [...groepen.values()].map(gatTekst).join(". ");
}

// ------------------------------------------------------------------ kaart

/**
 * De kleuren van core/maatschets.ts (STANDAARD_KLEUREN) naar de tokens van de site. Een regel in de
 * stylesheet gaat voor een attribuut, dus zo volgt de tekening het thema.
 */
export const KLEUR_TOKENS: Array<[string, string]> = [
  ["#211e18", "--inkt"],
  ["#6b6253", "--tekst-3"],
  ["#c7bba3", "--lijn-sterk"],
  ["#f5f0e6", "--papier-midden"],
  ["#b5793a", "--messing"],
  ["#fcf8f0", "--papier"],
];

const CSS = `
.viewer[data-bouwtekening] { position: relative; }
.bouwtekening { position: absolute; top: 12px; right: 12px; z-index: 15; width: min(400px, calc(100vw - 32px)); overflow: auto; padding: 14px 16px 16px; background: var(--papier-licht); border: 1px solid var(--lijn); border-top: 3px solid var(--messing); border-radius: var(--r-lg); box-shadow: var(--schaduw-zweef); color: var(--inkt); font-size: var(--fs-klein); line-height: var(--lh-klein); text-align: left; }
.bouwtekening:focus { outline: 0; }
.bouwtekening:focus-visible { box-shadow: var(--focus); }
.bt-kop { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px; align-items: start; }
.bt-kop .label { margin: 0; }
.bt-kop h3 { margin: 2px 0 0; font-family: var(--font-kop); font-size: 18px; line-height: var(--lh-kop); overflow-wrap: anywhere; }
.bt-maten { margin: 2px 0 0; font-family: var(--font-mono); font-size: var(--fs-klein); font-weight: 600; font-variant-numeric: tabular-nums; }
.bt-onder { margin: 0; color: var(--tekst-3); font-size: var(--fs-mini); }
.bt-schets { margin: 10px 0 0; padding: 8px; background: var(--papier); border: 1px solid var(--lijn-zacht); border-radius: var(--r-md); }
.bt-svg { display: block; width: 100%; height: auto; max-height: var(--bt-schets-max, none); }
.bt-svg text { font-family: var(--font-mono); }
${KLEUR_TOKENS.map(([kleur, token]) => `.bt-svg [fill="${kleur}"] { fill: var(${token}); }\n.bt-svg [stroke="${kleur}"] { stroke: var(${token}); }`).join("\n")}
.bt-zonder { margin: 0; color: var(--tekst-2); font-size: var(--fs-klein); line-height: 1.4; }
.bt-schets figcaption { margin-top: 6px; color: var(--tekst-3); font-size: var(--fs-mini); line-height: 1.4; }
.bt-groter { margin-top: 8px; }
.bt-groot { width: min(1000px, calc(100vw - 24px)); max-height: calc(100vh - 24px); padding: 14px 16px 16px; background: var(--papier-licht); border: 1px solid var(--lijn); border-top: 3px solid var(--messing); border-radius: var(--r-lg); color: var(--inkt); }
.bt-groot::backdrop { background: rgba(33, 30, 24, .5); }
.bt-groot .bt-kop h3 { margin: 0; }
.bt-groot-vlak { margin-top: 10px; overflow: auto; padding: 8px; background: var(--papier); border: 1px solid var(--lijn-zacht); border-radius: var(--r-md); }
/* Op een smal scherm schuift de tekening opzij in plaats van onleesbaar klein te worden. */
.bt-groot .bt-svg { min-width: 620px; max-height: none; }
.bt-groot p { margin: 8px 0 0; color: var(--tekst-3); font-size: var(--fs-mini); line-height: 1.4; }
.bouwtekening .kv { grid-template-columns: minmax(5.5rem, max-content) minmax(0, 1fr); margin: 10px 0 0; }
.bouwtekening .kv dt, .bouwtekening .kv dd { padding: 5px 0; }
.bouwtekening .kv dt { padding-right: 12px; line-height: 1.5; }
.bouwtekening .kv dd { font-size: var(--fs-klein); line-height: 1.4; }
.bt-actie { margin-top: 12px; }
.bt-actie .btn { width: 100%; justify-content: center; }
.bt-actie p { margin: 4px 0 0; color: var(--tekst-3); font-size: var(--fs-mini); line-height: 1.4; }
/* Breed scherm: de kaart zweeft als popup naast het model en mag over de rand van de viewer
   steken. Plek en hoogte zet de viewer (plaats()). De stage krijgt dan zelf de ronde hoeken. */
@media (min-width: 700px) {
  .viewer[data-bouwtekening] { overflow: visible; }
  :where(.viewer[data-bouwtekening]) > .viewer-stage { border-radius: calc(var(--r-lg) - 1px) 0 0 calc(var(--r-lg) - 1px); }
}
@media (min-width: 700px) and (max-width: 899px) {
  :where(.viewer[data-bouwtekening]) > .viewer-stage { border-radius: calc(var(--r-lg) - 1px) calc(var(--r-lg) - 1px) 0 0; }
}
@media (max-width: 699px) {
  .bouwtekening { position: static; grid-column: 1 / -1; width: auto; margin: 12px 0 0; box-shadow: none; }
}
`;

/** Afstand tot de rand van het venster en tot de kop van de site. */
const RAND = 12;
/** Zoveel px steekt de kaart links over het model; de rest ligt ernaast (als er ruimte is). */
const INZET = 56;
/** Kleiner wordt de tekening niet; past de kaart dan nog niet, dan scrolt hij. */
const MIN_SCHETS = 120;

let cssGeplaatst = false;

function plaatsCss() {
  if (cssGeplaatst) return;
  cssGeplaatst = true;
  const stijl = document.createElement("style");
  stijl.dataset.bron = "3d-bouwtekening";
  stijl.textContent = CSS;
  document.head.append(stijl);
}

function knop(tekst: string, klasse: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = klasse;
  b.textContent = tekst;
  return b;
}

function kvRij(dl: HTMLElement, dt: string, dd: string) {
  const div = document.createElement("div");
  const t = document.createElement("dt");
  t.textContent = dt;
  const d = document.createElement("dd");
  d.textContent = dd;
  div.append(t, d);
  dl.append(div);
}

let teller = 0;

export interface KaartActies {
  /** De kaart sluit (knop of Escape): de viewer zet de keuze uit. */
  gesloten(): void;
  /** "Laat zien hoe": naar de bouwstap met dit nummer. */
  laatZien(nr: number): void;
}

/** De kaart naast het model. Eén per viewer; toon() vult hem opnieuw. */
export class BouwtekeningKaart {
  private readonly el: HTMLElement;
  private readonly kopId = `bouwtekening-${++teller}`;
  private deel: OnderdeelTekening | null = null;
  /** De tekening bij dit deel, of null (gegevens zonder tekening). */
  private tekening: KaartTekening | null = null;
  private readonly stage: HTMLElement | null;
  /** De grote tekening ("Tekening groter"), gemaakt bij de eerste keer. */
  private groot: HTMLDialogElement | null = null;

  constructor(private readonly viewer: HTMLElement, private readonly acties: KaartActies) {
    plaatsCss();
    this.el = document.createElement("section");
    this.el.className = "bouwtekening";
    this.el.hidden = true;
    this.el.tabIndex = -1;
    this.el.setAttribute("role", "region");
    this.el.setAttribute("aria-labelledby", this.kopId);
    this.stage = viewer.querySelector<HTMLElement>(".viewer-stage");
    if (this.stage) this.stage.after(this.el);
    else viewer.append(this.el);
    document.addEventListener("keydown", (e) => {
      // Staat de grote tekening open, dan sluit Escape alleen die (dat doet de dialog zelf).
      if (e.key === "Escape" && !this.el.hidden && !this.groot?.open) {
        e.preventDefault();
        this.acties.gesloten();
      }
    });
    // Scrollen: de kaart schuift mee binnen het model, zodat hij in beeld blijft (O71).
    // Een lager venster zonder ander vak (werkbalk van de browser) vraagt een nieuwe hoogte.
    let gepland = false;
    let opnieuw = false;
    const volg = (hoogte: boolean) => {
      if (this.el.hidden) return;
      opnieuw ||= hoogte;
      if (gepland) return;
      gepland = true;
      requestAnimationFrame(() => {
        gepland = false;
        if (opnieuw) this.plaats();
        else this.zetPlek();
        opnieuw = false;
      });
    };
    document.addEventListener("scroll", () => volg(false), { passive: true, capture: true });
    window.addEventListener("resize", () => volg(true));
  }

  get open(): boolean {
    return !this.el.hidden;
  }

  /** Hoeveel px van de rechterkant van het vak de zwevende kaart bedekt; 0 als hij onder het model staat. */
  overlapMet(vak: HTMLElement): number {
    if (this.el.hidden || getComputedStyle(this.el).position !== "absolute") return 0;
    const k = this.el.getBoundingClientRect();
    const v = vak.getBoundingClientRect();
    if (k.top >= v.bottom || k.bottom <= v.top) return 0;
    return Math.max(0, Math.min(v.right, k.right) - Math.max(v.left, k.left));
  }

  /**
   * Vult de kaart voor dit onderdeel. Met `geo` (de driehoeken uit het 3D-model, mm) komt de vorm uit
   * het model als die klopt met de zaaglijst, zoals in de bouw-PDF.
   */
  toon(d: OnderdeelTekening, geo: Geometrie | null = null) {
    this.deel = d;
    this.tekening = kaartTekening(d, geo);
    this.vul();
    this.el.hidden = false;
    this.plaats();
  }

  verberg() {
    this.el.hidden = true;
    this.deel = null;
    this.tekening = null;
    if (this.groot?.open) this.groot.close();
  }

  /**
   * Dezelfde tekening groot, in een dialog over de pagina: zo breed als het venster toelaat (tot
   * 1000 px), en op een smal scherm minstens 620 px met een schuifbalk. Sluiten met de knop, met
   * Escape of met een klik naast de dialog. De focus gaat daarna terug naar de knop op de kaart.
   */
  private toonGroot(knopTerug: HTMLElement) {
    const d = this.deel;
    const t = this.tekening;
    if (!d || !t || !("svg" in t)) return;
    let dlg = this.groot;
    if (!dlg) {
      dlg = document.createElement("dialog");
      dlg.className = "bt-groot";
      dlg.setAttribute("aria-labelledby", `${this.kopId}-groot`);
      dlg.addEventListener("click", (e) => {
        if (e.target === dlg) dlg!.close();
      });
      document.body.append(dlg);
      this.groot = dlg;
    }
    dlg.replaceChildren();
    const kop = document.createElement("div");
    kop.className = "bt-kop";
    const h = document.createElement("h3");
    h.id = `${this.kopId}-groot`;
    h.textContent = `Bouwtekening van ${d.naam}`;
    const sluit = knop("Sluiten", "btn btn-secondary btn-sm");
    sluit.addEventListener("click", () => dlg!.close());
    kop.append(h, sluit);
    const vlak = document.createElement("div");
    vlak.className = "bt-groot-vlak";
    const svg = svgElement(t.svg, d.naam);
    if (svg) vlak.append(svg);
    const noot = document.createElement("p");
    noot.textContent = t.noot;
    dlg.append(kop, vlak, noot);
    // Terug naar de knop, maar niet als de kaart intussen dicht is (verberg() sloot de dialog).
    dlg.addEventListener("close", () => {
      if (!this.el.hidden && knopTerug.isConnected) knopTerug.focus();
    }, { once: true });
    dlg.showModal();
    sluit.focus();
  }

  /**
   * Breed scherm: de kaart zweeft rechts naast het model en steekt INZET px over het vak. Past hij
   * daar niet in het venster, dan schuift hij naar links tot hij erin past. De hoogte past in het
   * venster onder de kop van de site; zo nodig wordt de tekening kleiner en scrolt de kaart zelf. Op
   * een smal scherm staat de kaart onder het model en doet dit niets.
   */
  plaats() {
    const s = this.el.style;
    s.removeProperty("--bt-schets-max");
    s.left = s.top = s.right = s.maxHeight = "";
    if (!this.zweeft()) return;

    const ruimte = window.innerHeight - this.boven() - RAND;
    const teVeel = this.el.offsetHeight - ruimte;
    const svg = this.el.querySelector(".bt-svg");
    if (teVeel > 0 && svg) s.setProperty("--bt-schets-max", `${Math.max(MIN_SCHETS, svg.getBoundingClientRect().height - teVeel)}px`);
    s.maxHeight = `${Math.max(ruimte, 200)}px`;
    this.zetPlek();
  }

  /**
   * Zet de kaart op zijn plek voor de huidige scrolstand. Bovenaan het model als dat in beeld is.
   * Scrol je verder, dan schuift de kaart mee tot de onderkant van het model, zoals een sticky kop.
   */
  zetPlek() {
    if (!this.zweeft() || !this.stage) return;
    const s = this.el.style;
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    const b = this.el.offsetWidth;
    const h = this.el.offsetHeight;
    const st = this.stage.getBoundingClientRect();
    const x = Math.max(RAND, Math.min(st.right - INZET, vw - RAND - b));
    const bovenModel = st.top + RAND;
    // Liefst helemaal in het venster, onder de kop van de site.
    const liefst = Math.max(this.boven(), Math.min(bovenModel, vh - RAND - h));
    // Maar de kaart blijft bij het model: niet verder omlaag dan de onderkant van het model en niet
    // verder omhoog dan de bovenkant (een hoge kaart mag tot zijn onderkant bij die van het model).
    // Staat de bovenkant van het model in beeld, dan mag hij omhoog tot hij onderaan in beeld past.
    let hoogste = Math.min(bovenModel, st.bottom - h);
    if (bovenModel < vh) hoogste = Math.min(hoogste, vh - RAND - h);
    const laagste = Math.max(bovenModel, st.bottom - h);
    const y = Math.min(laagste, Math.max(hoogste, liefst));
    const v = this.viewer.getBoundingClientRect();
    s.right = "auto";
    s.left = `${Math.round(x - v.left - this.viewer.clientLeft)}px`;
    s.top = `${Math.round(y - v.top - this.viewer.clientTop)}px`;
  }

  /** true als de kaart open is en naast het model zweeft (breed scherm). */
  private zweeft(): boolean {
    return !this.el.hidden && !!this.stage && getComputedStyle(this.el).position === "absolute";
  }

  /** De bovenkant van het vrije venster: onder de kop van de site. */
  private boven(): number {
    const kop = document.querySelector(".site-header")?.getBoundingClientRect();
    return Math.max(0, kop?.bottom ?? 0) + RAND;
  }

  private vul() {
    const d = this.deel;
    if (!d) return;
    this.el.replaceChildren();

    const kop = document.createElement("div");
    kop.className = "bt-kop";
    const tekst = document.createElement("div");
    const label = document.createElement("span");
    label.className = "label";
    label.textContent = "Bouwtekening";
    const h = document.createElement("h3");
    h.id = this.kopId;
    h.textContent = d.naam;
    const maten = document.createElement("p");
    maten.className = "bt-maten";
    maten.textContent = matenTekst(d);
    const onder = document.createElement("p");
    onder.className = "bt-onder";
    onder.textContent = `${d.diameter_mm !== null ? "Diameter × dikte" : "Lengte × breedte × dikte"}${d.aantal > 1 ? `, ${d.aantal} stuks` : ""}`;
    tekst.append(label, h, maten, onder);
    const sluit = knop("Sluiten", "btn btn-secondary btn-sm");
    sluit.setAttribute("aria-label", "Sluit de bouwtekening");
    sluit.addEventListener("click", () => this.acties.gesloten());
    kop.append(tekst, sluit);
    this.el.append(kop);

    const t = this.tekening;
    if (t) {
      const fig = document.createElement("figure");
      fig.className = "bt-schets";
      if ("zonder" in t) {
        const p = document.createElement("p");
        p.className = "bt-zonder";
        p.textContent = t.zonder;
        fig.append(p);
      } else {
        const svg = svgElement(t.svg, d.naam);
        if (svg) fig.append(svg);
        const noot = document.createElement("figcaption");
        noot.textContent = t.noot;
        fig.append(noot);
        if (svg) {
          const groter = knop("Tekening groter", "btn btn-secondary btn-sm bt-groter");
          groter.setAttribute("aria-haspopup", "dialog");
          groter.addEventListener("click", () => this.toonGroot(groter));
          fig.append(groter);
        }
      }
      this.el.append(fig);
    }

    const dl = document.createElement("dl");
    dl.className = "kv";
    kvRij(dl, "Materiaal", d.materiaal);
    if (d.uit) kvRij(dl, "Uit", d.uit);
    if (d.afwerking) kvRij(dl, "Afwerking", d.afwerking);
    if (d.uiteinden) kvRij(dl, "Uiteinden", d.uiteinden);
    const bewerking = bewerkingTekst(d.bewerking);
    if (bewerking) kvRij(dl, "Bewerking", bewerking);
    if (d.gaten.length || d.gatplekken?.length) kvRij(dl, "Gaten", gatenTekst(d));
    this.el.append(dl);

    if (d.stap) {
      const actie = document.createElement("div");
      actie.className = "bt-actie";
      const b = knop("Laat zien hoe", "btn btn-donker");
      const nr = d.stap.nr;
      b.addEventListener("click", () => this.acties.laatZien(nr));
      const p = document.createElement("p");
      p.textContent = `Stap ${nr}: ${d.stap.tekst}`;
      actie.append(b, p);
      this.el.append(actie);
    }
  }
}
